/**
 * useComposerSize — measure the composer dock and publish its height
 * as the `--composer-h` CSS custom property on the chatroom stack.
 *
 * Single responsibility: keep `--composer-h` in sync with the dock's
 * border-box height. Consumers that need the value (the slot's
 * `padding-bottom` calc, the ↓-button's `bottom` calc, the empty-state
 * welcome's `padding-bottom`) read the var via inheritance. No JS-side
 * value is exposed — CSS is the SSOT.
 *
 * Why a custom property and not React state:
 *   - The value is only ever consumed by CSS calcs.
 *   - Re-rendering React on every composer-resize tick (textarea grow,
 *     keyboard show/hide, attach chip in/out) would invalidate the
 *     entire chat tree per frame. The custom property is mutated
 *     imperatively against the DOM and the affected calcs re-resolve
 *     on the browser's next layout pass — zero React re-renders.
 *
 * Lifecycle:
 *   - The returned `attachComposer` ref-callback runs in React's
 *     commit phase, BEFORE useLayoutEffect. Initial paint already
 *     has the measured value, no fallback flash.
 *   - The internal ResizeObserver tracks every subsequent grow/shrink
 *     (textarea reflow, viewport rotate, sidebar collapse, safe-area
 *     change on iOS keyboard show/hide).
 */

import { useCallback, useEffect, useRef } from 'react';


const COMPOSER_HEIGHT_PROPERTY = '--composer-h';

/** Walks up from the composer element to the chatroom stack so the
 *  custom property is set on a stable ancestor that survives Virtuoso
 *  re-mounts on chat switch. */
const STACK_SELECTOR = '.chatroom__stack';


export interface UseComposerSizeReturn {
    /** Ref-callback to spread on the dock element. */
    attachComposer: (el: HTMLDivElement | null) => void;
    /** Live ref into the dock element (read for one-off measurements). */
    composerRef: React.MutableRefObject<HTMLDivElement | null>;
}


/** Measures the chat composer dock's border-box height and publishes
 *  it as `--composer-h` on the nearest `.chatroom__stack` ancestor. */
export function useComposerSize(): UseComposerSizeReturn {
    const composerRef = useRef<HTMLDivElement | null>(null);

    /** First write happens here (commit phase). closest() resolves at
     *  ref-attach time, when the parent stack is already in the DOM
     *  even if React hasn't yet attached its own ref to it (refs
     *  attach child-first / post-order). */
    const attachComposer = useCallback((el: HTMLDivElement | null) => {
        composerRef.current = el;
        if (!el) return;
        const stack = el.closest(STACK_SELECTOR) as HTMLElement | null;
        stack?.style.setProperty(
            COMPOSER_HEIGHT_PROPERTY,
            `${el.offsetHeight}px`,
        );
    }, []);

    /** Track every subsequent resize. borderBoxSize is preferred over
     *  contentRect because it includes the form's padding (which the
     *  visually-occupied dock height needs) and matches the
     *  offsetHeight measurement used by attachComposer above —
     *  consistent value across initial-paint and steady-state. */
    useEffect(() => {
        const composer = composerRef.current;
        if (!composer) return;
        const stack = composer.closest(STACK_SELECTOR) as HTMLElement | null;
        if (!stack) return;

        // Belt-and-suspenders initial write — handles any race where
        // the ref-callback didn't run before this effect.
        stack.style.setProperty(
            COMPOSER_HEIGHT_PROPERTY,
            `${composer.offsetHeight}px`,
        );

        const ro = new ResizeObserver(([entry]) => {
            if (!entry) return;
            const h = Math.ceil(
                entry.borderBoxSize?.[0]?.blockSize
                ?? (entry.target as HTMLElement).offsetHeight,
            );
            stack.style.setProperty(COMPOSER_HEIGHT_PROPERTY, `${h}px`);
        });
        ro.observe(composer);
        return () => ro.disconnect();
    }, []);

    return { attachComposer, composerRef };
}
